import React, { FC, useState, useCallback, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import type {
  BillingPaymentSourceSummary,
  CardSetupCheckoutResponse,
  PaymentSourcesResponse,
  SyncPaymentSourcesResponse,
} from '@dreamai/types';
import api from '../../api';
import {
  AirwallexCardForm,
  CardSelect,
  NEW_CARD,
} from '../chat/sidebar/AddFundsTray';
import './top-up-pot-modal.scss';

interface TopUpPotModalProps {
  isOpen: boolean;
  onClose: () => void;
  topUpOption: {
    priceId: string;
    unitAmount: number;
    unitsPerProduct: number;
    currency: string;
  } | null;
  assistantName: string;
  onSuccess: () => void;
}

function formatCurrency(amount: number, currency: string): string {
  try {
    const isWhole = amount % 1 === 0;
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency,
      minimumFractionDigits: isWhole ? 0 : 2,
      maximumFractionDigits: 2,
    }).format(amount);
  } catch {
    return `${currency} ${amount.toFixed(2)}`;
  }
}

function formatCurrencySymbol(currency: string): string {
  try {
    return (
      new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      })
        .formatToParts(0)
        .find((part) => part.type === 'currency')?.value ?? `${currency} `
    );
  } catch {
    return `${currency} `;
  }
}

/**
 * Settings → Assistant's Card top-up. The user picks the amount and the card
 * that pays for it; a new card is added inline through Airwallex's embedded
 * form (the card never passes through Dream).
 */
const TopUpPotModal: FC<TopUpPotModalProps> = ({
  isOpen,
  onClose,
  topUpOption,
  assistantName,
  onSuccess,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [amount, setAmount] = useState('20');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cards, setCards] = useState<BillingPaymentSourceSummary[] | null>(
    null,
  );
  const [cardId, setCardId] = useState<string>('');
  const [formSettled, setFormSettled] = useState(false);

  const authedPost = useCallback(
    async <T,>(path: string, body: Record<string, unknown>): Promise<T> => {
      const token = await getAccessTokenSilently();
      const { data } = await api.post<T>(path, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return data;
    },
    [getAccessTokenSilently],
  );

  const handleClose = useCallback(() => {
    setAmount('20');
    setError(null);
    onClose();
  }, [onClose]);

  const loadCards = useCallback(async () => {
    try {
      const data = await authedPost<PaymentSourcesResponse>(
        '/credits/payment-sources',
        {},
      );
      const list = data?.cards ?? [];
      setCards(list);
      setCardId(
        (current) =>
          (current && list.some((c) => c.id === current)
            ? current
            : (list.find((c) => c.isDefault) ?? list[0])?.id) ?? NEW_CARD,
      );
    } catch (err) {
      console.error('Error loading payment sources:', err);
      setCards([]);
      setCardId(NEW_CARD);
    }
  }, [authedPost]);

  useEffect(() => {
    if (isOpen) {
      void loadCards();
    } else {
      setCards(null);
      setCardId('');
      setFormSettled(false);
    }
  }, [isOpen, loadCards]);

  const startSetup = useCallback(async () => {
    const data = await authedPost<CardSetupCheckoutResponse>(
      '/credits/payment-sources/setup',
      { returnUrl: window.location.href },
    );
    if (!data.success || !data.clientSecret || !data.env) {
      throw new Error(data.error || 'Failed to start card setup');
    }
    return { clientSecret: data.clientSecret, env: data.env };
  }, [authedPost]);

  const handleCardSaved = useCallback(async () => {
    try {
      const data = await authedPost<SyncPaymentSourcesResponse>(
        '/credits/payment-sources/sync',
        { knownIds: (cards ?? []).map((c) => c.id) },
      );
      setCards(data.cards);
      const next =
        data.newCard ?? data.cards.find((c) => c.isDefault) ?? data.cards[0];
      if (next) setCardId(next.id);
    } catch (err) {
      console.error('Error syncing payment sources:', err);
      setError('Your card was saved but could not be loaded. Please retry.');
      void loadCards();
    }
  }, [authedPost, cards, loadCards]);

  const parsedAmount = parseInt(amount, 10) || 0;
  const quantity = topUpOption
    ? Math.max(1, Math.ceil(parsedAmount / topUpOption.unitAmount))
    : 1;
  const totalCost = topUpOption ? topUpOption.unitAmount * quantity : 0;
  const addingCard = cardId === NEW_CARD;

  const handleConfirm = useCallback(async () => {
    if (!topUpOption || submitting || parsedAmount <= 0 || addingCard) return;
    setSubmitting(true);
    setError(null);
    try {
      const data = await authedPost<{ success: boolean; error?: string }>(
        '/credits/topup-balance',
        { priceId: topUpOption.priceId, quantity, paymentSourceId: cardId },
      );
      if (data.success) {
        setAmount('20');
        onSuccess();
      } else {
        setError(data.error || 'Top-up failed. Please try again.');
      }
    } catch (err: any) {
      console.error('Error topping up pot:', err);
      setError(
        err?.response?.data?.error || 'Top-up failed. Please try again.',
      );
      // The card may have been removed since we listed it — refresh the list.
      void loadCards();
    } finally {
      setSubmitting(false);
    }
  }, [
    topUpOption,
    quantity,
    parsedAmount,
    submitting,
    addingCard,
    cardId,
    authedPost,
    onSuccess,
    loadCards,
  ]);

  if (!isOpen || !topUpOption) return null;

  return (
    <div className='topup-pot-overlay' onClick={handleClose}>
      <div
        className={`topup-pot-modal csd-funding ${addingCard ? 'topup-pot-modal--add-card' : ''}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className='topup-pot-header'>
          <p className='topup-pot-title'>Top up {assistantName}&rsquo;s Card</p>
          <button
            className='topup-pot-close'
            onClick={handleClose}
            aria-label='Close'
          >
            <svg
              width='20'
              height='20'
              viewBox='0 0 24 24'
              fill='none'
              stroke='#374562'
              strokeWidth='2'
              strokeLinecap='round'
              strokeLinejoin='round'
            >
              <line x1='18' y1='6' x2='6' y2='18' />
              <line x1='6' y1='6' x2='18' y2='18' />
            </svg>
          </button>
        </div>
        <div className='topup-pot-body'>
          <p className='topup-pot-desc'>
            Add funds to {assistantName}&rsquo;s Card. Your payment card will be
            charged immediately.
          </p>
          <div className='topup-pot-amount-row'>
            <span className='topup-pot-amount-label'>Amount</span>
            <div className='topup-pot-stepper'>
              <button
                className='topup-pot-step-btn'
                onClick={() => {
                  const step = parsedAmount <= 10 ? 1 : 10;
                  setAmount(String(Math.max(0, parsedAmount - step)));
                }}
                disabled={parsedAmount <= 0}
                aria-label='Decrease amount'
              >
                −
              </button>
              <div className='topup-pot-input-wrap'>
                <span className='topup-pot-currency-symbol'>
                  {formatCurrencySymbol(topUpOption.currency)}
                </span>
                <input
                  className='topup-pot-input'
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  placeholder='0'
                  value={amount}
                  onChange={(e) => {
                    const v = e.target.value.replace(/[^0-9]/g, '');
                    setAmount(v);
                  }}
                  autoFocus
                />
              </div>
              <button
                className='topup-pot-step-btn'
                onClick={() => {
                  const step = parsedAmount < 10 ? 1 : 10;
                  setAmount(String(parsedAmount + step));
                }}
                aria-label='Increase amount'
              >
                +
              </button>
            </div>
          </div>
          <div className='topup-pot-card-row'>
            <span className='topup-pot-amount-label'>Pay with</span>
            {cards === null ? (
              <span className='topup-pot-cards-loading'>
                Loading your cards…
              </span>
            ) : (
              <CardSelect cards={cards} value={cardId} onChange={setCardId} />
            )}
          </div>
          {addingCard && cards !== null && (
            <div
              className={`topup-pot-add-card ${formSettled ? 'topup-pot-add-card--ready' : ''}`}
            >
              {!formSettled && (
                <p className='topup-pot-cards-loading'>Loading secure form…</p>
              )}
              <AirwallexCardForm
                startSetup={startSetup}
                onSaved={() => void handleCardSaved()}
                onSettled={() => setFormSettled(true)}
              />
            </div>
          )}
          {error && <p className='topup-pot-error'>{error}</p>}
        </div>
        <div className='topup-pot-footer'>
          <button className='topup-pot-cancel' onClick={handleClose}>
            Cancel
          </button>
          {!addingCard && (
            <button
              className='topup-pot-confirm'
              onClick={handleConfirm}
              disabled={submitting || parsedAmount <= 0 || cards === null}
            >
              {submitting
                ? 'Processing...'
                : `Top up ${parsedAmount > 0 ? formatCurrency(totalCost, topUpOption.currency) : ''}`}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TopUpPotModal;
